import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import H1 from '@/components/base/Headings/H1';
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import LoaderPage from '@/components/base/Loader/LoaderPage';
import Tooltip from '@/components/base/Tooltip';
import Iconosquare from '@/components/icons/Iconosquare';
import BackgroundLayers from '@/components/layouts/BackgroundLayers/BackgroundLayers';
import ErrorFallbackGlobal from '@/components/layouts/ErrorFallbacks/ErrorFallbackGlobal';
// Components
import SWRConfigWrapper from '@/components/layouts/SWRConfigWrapper';
const ModalDeleteAccount = dynamic(
    () => import('@/components/composite/Modals/ModalDeleteAccount')
);

// Hooks
import { useGlobalInfo } from '@/utils/hooks/context/useGlobalInfo';
import useCurrentSession from '@/utils/hooks/user/useCurrentSession';
import useLogout from '@/utils/hooks/user/useLogout';

// Utils
import useMarketingUrl from '@/utils/urls/useMarketingUrl';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';
import useToggle from '@/utils/hooks/utils/useToggle';

// Type
import type { StepType } from '@/lib/signup/steps';
import { steps } from '@/lib/signup/steps';
// Lib
import { PENDO_CLASS_SIGNUP_CONTACT_US } from '@/lib/tools/pendo/class';

type LayoutSignUpLoggedProps = {
    children: ReactNode;
};

const LayoutSignUpLogged = ({ children }: LayoutSignUpLoggedProps) => {
    const { useRemoveDarkMode } = useDarkMode();
    useRemoveDarkMode();

    return (
        <SWRConfigWrapper>
            <ContentWrapper>{children}</ContentWrapper>
        </SWRConfigWrapper>
    );
};

const ContentWrapper = ({ children }: LayoutSignUpLoggedProps) => {
    const { isLoading, user } = useCurrentSession();

    if (isLoading || !user) {
        return <LoaderPage />;
    }

    return (
        <div className="relative flex min-h-screen md:h-screen">
            <div className="relative mx-auto min-h-full w-full max-w-[100rem] p-m pb-10 md:h-full md:p-10">
                <ErrorBoundary FallbackComponent={ErrorFallbackGlobal}>
                    <div className="flex min-h-full w-full flex-col overflow-hidden rounded-md bg-white md:h-full md:space-y-8">
                        <Header />
                        <main className="flex flex-col md:h-[calc(100%-146px)] md:flex-row">
                            {children}
                        </main>
                    </div>
                </ErrorBoundary>
                <Footer />
            </div>
            <div className="absolute inset-0 -z-1 overflow-hidden">
                <BackgroundLayers className="absolute inset-0 h-full min-h-full w-full" />
            </div>
        </div>
    );
};

type MainContentProps = {
    title: ReactNode;
    subtitle: ReactNode;
    headerChildren?: ReactNode;
    className?: string;
    children: ReactNode;
};

const MainContent = ({
    title,
    subtitle,
    headerChildren,
    className,
    children
}: MainContentProps) => {
    return (
        <div
            className={clsx(
                'scrollbar relative flex w-full flex-1 flex-col px-6 pt-10 xs:px-10 md:px-20 lg:h-full lg:px-32',
                className
            )}
            id="layout-scrollable-content">
            {headerChildren}
            <H1 className="mb-4" data-testid="signup-step-title">
                {title}
            </H1>
            <p
                className="mb-12 font-GMedium text-xl text-icoGray-500"
                data-testid="signup-step-subtitle">
                {subtitle}
            </p>
            {children}
        </div>
    );
};

LayoutSignUpLogged.MainContent = MainContent;

type SideContentProps = {
    hideOnMobile?: boolean;
    children: ReactNode;
};

const SideContent = ({ hideOnMobile = true, children }: SideContentProps) => {
    return (
        <div
            className={clsx('w-full overflow-hidden lg:h-full lg:w-1/3 lg:rounded-tl-md', {
                'hidden lg:block': hideOnMobile
            })}>
            {children}
        </div>
    );
};

LayoutSignUpLogged.SideContent = SideContent;

const Header = () => {
    const { t } = useTranslation('commons');
    const { logout } = useLogout();
    const { globalInfo } = useGlobalInfo();

    const current = globalInfo.signup.currentStep;
    const currentIndex = steps.findIndex((el) => current === el.step);

    return (
        <header
            className={clsx(
                'relative flex flex-row items-center justify-between space-x-3 p-6 before:absolute before:left-0 before:top-0 before:h-[3px] before:w-full before:bg-icoBlue-200 after:absolute after:left-0 after:top-0 after:block after:h-[3px] after:bg-icoBlue xs:space-x-10 xs:p-10',
                {
                    'after:w-1/4': currentIndex === 0,
                    'after:w-1/2': currentIndex === 1,
                    'after:w-3/4': currentIndex === 2,
                    'after:w-full': currentIndex === 3 || currentIndex === -1
                }
            )}
            aria-label="Progress">
            <span>
                <Iconosquare size={35} className="text-icoBlue md:block" />
            </span>
            {currentIndex > 0 && currentIndex !== 3 ? (
                <div className="flex flex-row space-x-3 xs:space-x-5 lg:space-x-10">
                    {steps.map(({ content, id }, index) => {
                        return (
                            <Step
                                key={id}
                                id={id}
                                oldStep={index < currentIndex}
                                currentStep={index === currentIndex}
                                nextStep={index > currentIndex}
                                content={content}
                            />
                        );
                    })}
                </div>
            ) : null}
            <Tooltip
                content={t('logout')}
                position="top-start"
                variant="menu"
                arrow={false}
                offset={[17, -7]}>
                <button onClick={logout} className="leading-none">
                    <Icon icon="logout" className="align-middle text-sm text-icoGray-400" />
                </button>
            </Tooltip>
        </header>
    );
};

type StepProps = Omit<StepType, 'step'> & {
    currentStep: boolean;
    oldStep: boolean;
    nextStep: boolean;
};

const Step = ({ id, currentStep, oldStep, nextStep, content, ...restProps }: StepProps) => {
    const { t } = useTranslation('signup_funnel');

    return (
        <div
            aria-current={currentStep ? 'step' : null}
            className={clsx('flex items-center space-x-3', {
                'flex-1 sm:flex-none': currentStep
            })}
            {...restProps}>
            <div
                className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-full border bg-white/80',
                    {
                        'border-icoBlue-200': currentStep,
                        'border-icoGray-300': nextStep || oldStep
                    }
                )}>
                {currentStep || nextStep ? (
                    <span
                        className={clsx('text-sm', {
                            'text-icoBlue': currentStep,
                            'text-icoBlack': nextStep
                        })}>
                        {id}
                    </span>
                ) : null}
                {oldStep ? <Icon icon="check" className="text-sm text-icoGray-400" /> : null}
            </div>
            <div
                className={clsx('hidden flex-1 flex-col justify-center font-GSemiBold', {
                    'lg:flex': nextStep || oldStep,
                    'sm:flex': currentStep
                })}>
                <p
                    className={clsx('space-x-0.5 font-GMedium text-xxs', {
                        'text-icoBlue': currentStep,
                        'text-icoGray-400': !currentStep
                    })}>
                    <span>{t('step')}</span>
                    <span>{id}</span>
                </p>
                <p
                    className={clsx('break-words text-sm', {
                        'text-black': currentStep,
                        'text-icoGray-400': !currentStep
                    })}>
                    {t(content)}
                </p>
            </div>
        </div>
    );
};

const Footer = () => {
    const { marketingUrl } = useMarketingUrl();
    const { t } = useTranslation('signup_funnel');
    const { isOpen: isOpenModalDeleteAccount, toggle: toggleModalDeleteAccount } = useToggle();
    return (
        <footer className="bottom-0 left-0 w-full md:absolute">
            <ul className="flex h-10 flex-row items-center justify-end space-x-5 font-GMedium text-xs text-icoGray-500 md:px-10">
                <LinkBase
                    key="contact"
                    target="_blank"
                    href={marketingUrl('contact')}
                    className={PENDO_CLASS_SIGNUP_CONTACT_US}
                    rel="noopener noreferrer">
                    {t('need_help')}
                </LinkBase>
                <button onClick={toggleModalDeleteAccount}>{t('delete_account')}</button>
            </ul>
            <ModalDeleteAccount
                isOpen={isOpenModalDeleteAccount}
                toggle={toggleModalDeleteAccount}
            />
        </footer>
    );
};

export default LayoutSignUpLogged;
